import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const NigeriansVaccinationInterests = () => {
  // DonutChart component matching HEPT dashboard architecture
  const DonutChart = ({ data, colors, title, subtitle, size = 200 }) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center">
          <h4 className="text-sm font-medium text-gray-800 mb-2">{title}</h4>
          <div className="text-xs text-gray-500">No data available</div>
        </div>
      );
    }

    const validData = data.filter(d => d.value > 0 && !isNaN(d.value));
    if (validData.length === 0) {
      return (
        <div className="text-center">
          <h4 className="text-sm font-medium text-gray-800 mb-2">{title}</h4>
          <div className="text-xs text-gray-500">No valid data to display</div>
        </div>
      );
    }

    const total = validData.reduce((sum, d) => sum + d.value, 0);
    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = size * 0.35;
    const innerRadius = size * 0.2;

    const createArcPath = (startAngle, endAngle, outerR, innerR) => {
      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + outerR * Math.cos(startAngleRad);
      const y1 = centerY + outerR * Math.sin(startAngleRad);
      const x2 = centerX + outerR * Math.cos(endAngleRad);
      const y2 = centerY + outerR * Math.sin(endAngleRad);

      const x3 = centerX + innerR * Math.cos(endAngleRad);
      const y3 = centerY + innerR * Math.sin(endAngleRad);
      const x4 = centerX + innerR * Math.cos(startAngleRad);
      const y4 = centerY + innerR * Math.sin(startAngleRad);

      const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

      return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
    };

    let currentAngle = -90;

    return (
      <div className="text-center">
        <h4 className="text-sm font-medium text-gray-800 mb-2">{title}</h4>
        {subtitle && <p className="text-xs text-gray-600 mb-3">{subtitle}</p>}
        
        <svg width={size} height={size} className="mx-auto mb-3">
          {validData.map((segment, index) => {
            const angle = (segment.value / total) * 360;
            const endAngle = currentAngle + angle;
            
            const path = createArcPath(currentAngle, endAngle, outerRadius, innerRadius);
            currentAngle = endAngle;
            
            return (
              <path
                key={index}
                d={path}
                fill={colors[index] || '#94A3B8'}
                stroke="#ffffff"
                strokeWidth="1"
              />
            );
          })}
          
          <text
            x={centerX}
            y={centerY - 3}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-800"
            dominantBaseline="middle"
          >
            {total.toFixed(1)}%
          </text>
          <text
            x={centerX}
            y={centerY + 10}
            textAnchor="middle"
            className="text-xs fill-gray-600"
            dominantBaseline="middle"
          >
            Total
          </text>
        </svg>

        <div className="space-y-1">
          {validData.map((segment, index) => (
            <div key={index} className="flex items-center text-xs">
              <div
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: colors[index] || '#94A3B8' }}
              ></div>
              <span className="text-gray-700 mr-1" title={segment.label}>
                {segment.label}:
              </span>
              <span className="font-medium text-gray-800">
                {segment.value.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // askNivi journey color palette for consistency
  const colors = {
    // High readiness - strong blue
    highReadiness: '#4361EE',
    // Medium readiness - teal for education opportunities
    mediumReadiness: '#4FBFA5',
    // Low readiness - light gray for minimal engagement
    lowReadiness: '#E5E7EB',
    // Payment tiers
    basicCare: '#4FBFA5',
    standardCare: '#FFC20E',
    premiumCare: '#F37021',
    // Service delivery
    hospitals: '#9E7BFF',
    home: '#005BE7',
    provider: '#BA55D3',
    // Demographics
    age15_24: '#4FBFA5',
    age25_34: '#4361EE',
    age35_45: '#F37021',
    // Intent levels
    definitely: '#4361EE',
    probably: '#4FBFA5',
    might: '#FFC20E',
    unlikely: '#E5E7EB'
  };

  // MRP-Adjusted Behavioral Intent Data from Step 5 Final Analysis
  const readinessLevels = [
    { label: 'High Readiness', value: 70.3, description: 'Direct conversion ready (~91,964 individuals)', color: colors.highReadiness },
    { label: 'Medium Readiness', value: 27.6, description: 'Education intervention targets (~36,133 individuals)', color: colors.mediumReadiness },
    { label: 'Low Readiness', value: 2.1, description: 'Intensive engagement required (~2,774 individuals)', color: colors.lowReadiness }
  ];

  // High Readiness Payment Preferences (MRP-Adjusted)
  const paymentPreferences = [
    { label: 'Basic Care', value: 75.2, color: colors.basicCare },
    { label: 'Standard Care', value: 13.8, color: colors.standardCare },
    { label: 'Premium Protection', value: 11.0, color: colors.premiumCare }
  ];

  // Service Delivery Preferences
  const serviceDelivery = [
    { label: 'Hospitals', value: 45.4, color: colors.hospitals },
    { label: 'Home Services', value: 26.0, color: colors.home },
    { label: 'Provider Clinics', value: 19.3, color: colors.provider },
    { label: 'Other', value: 9.3, color: colors.lowReadiness }
  ];

  // Age-Based Readiness Progression
  const ageReadiness = [
    { age: '15-24', highReadiness: 61.6, mediumReadiness: 35.2, lowReadiness: 3.2, population: '38%' },
    { age: '25-34', highReadiness: 74.9, mediumReadiness: 23.6, lowReadiness: 1.5, population: '40%' },
    { age: '35-45', highReadiness: 78.7, mediumReadiness: 19.8, lowReadiness: 1.5, population: '22%' }
  ];

  // Vaccination Intent Levels
  const vaccinationIntent = [
    { label: 'Definitely Would', value: 59.0, color: colors.definitely },
    { label: 'Probably Would', value: 12.8, color: colors.probably },
    { label: 'Might Go', value: 16.5, color: colors.might },
    { label: 'Unlikely', value: 11.7, color: colors.unlikely }
  ];

  // Gender Readiness Patterns
  const genderReadiness = [
    { gender: 'Women', highReadiness: 84.4, mediumReadiness: 14.1, lowReadiness: 1.5, population: '51%' },
    { gender: 'Men', highReadiness: 55.0, mediumReadiness: 43.1, lowReadiness: 1.9, population: '49%' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
          <h2 className="text-2xl font-light text-center text-gray-800 mb-8">
            Nigeria HPV Vaccination Behavioral Intent Analysis
          </h2>
          <p className="text-center text-gray-600 text-sm mb-6">
            MRP Population-Adjusted Results • Lagos & Ogun State • Urban Residents Ages 15-45
          </p>

          {/* Key Findings - Updated with MRP-Adjusted Data */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-6 text-center">Key Market Insights</h3>
            
            <div className="flex justify-center items-center space-x-16 mb-6">
              <div className="text-center">
                <div className="text-5xl font-light mb-2" style={{ color: colors.highReadiness }}>70.3%</div>
                <div className="text-sm text-gray-600">High Vaccination Readiness</div>
                <div className="text-xs text-gray-500">~91,964 individuals ready</div>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-light mb-2" style={{ color: colors.mediumReadiness }}>97.2%</div>
                <div className="text-sm text-gray-600">Prevention Interest</div>
                <div className="text-xs text-gray-500">Universal health motivation</div>
              </div>
              
              <div className="text-center">
                <div className="text-5xl font-light mb-2" style={{ color: colors.premiumCare }}>25.4</div>
                <div className="text-sm text-gray-600">Point Education Gap</div>
                <div className="text-xs text-gray-500">Prevention ↔ Vaccination</div>
              </div>
            </div>

            <div className="text-center text-sm text-gray-700">
              <strong>Market Opportunity:</strong> 128,084 individuals showing positive vaccination intent (16.4% of target population)
            </div>
          </div>

          {/* Population-Representative Readiness Distribution */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Population-Representative Readiness Distribution</h3>
            
            <div className="flex justify-center items-start space-x-8 mb-6">
              <div className="text-center">
                <DonutChart
                  data={readinessLevels}
                  colors={[colors.highReadiness, colors.mediumReadiness, colors.lowReadiness]}
                  title="Vaccination Readiness Levels"
                  subtitle="MRP Population-Adjusted"
                  size={240}
                />
              </div>
              
              <div className="flex-1 max-w-md">
                <div className="space-y-4">
                  {readinessLevels.map((level, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <div
                          className="w-3 h-3 rounded-full mr-3"
                          style={{ backgroundColor: level.color }}
                        ></div>
                        <span className="font-medium text-gray-800">{level.label}: {level.value}%</span>
                      </div>
                      <p className="text-sm text-gray-600 ml-6">{level.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Age Progression Analysis */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Age-Based Readiness Progression</h3>
            
            <div className="mb-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ageReadiness} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="age" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [`${value.toFixed(1)}%`, name]} />
                  <Legend />
                  <Bar dataKey="highReadiness" stackId="a" fill={colors.highReadiness} name="High Readiness" />
                  <Bar dataKey="mediumReadiness" stackId="a" fill={colors.mediumReadiness} name="Medium Readiness" />
                  <Bar dataKey="lowReadiness" stackId="a" fill={colors.lowReadiness} name="Low Readiness" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="text-center text-sm text-gray-700">
              <strong>Clear Age Pattern:</strong> Readiness increases with age from 61.6% (15-24) → 74.9% (25-34) → 78.7% (35-45)
            </div>
          </div>

          {/* Gender Analysis */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Gender Readiness Dynamics</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {genderReadiness.map((data, index) => (
                <div key={index} className="text-center">
                  <h4 className="font-medium text-gray-800 mb-3">{data.gender} ({data.population} of population)</h4>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <span className="text-sm">High Readiness:</span>
                      <span className="font-medium" style={{ color: colors.highReadiness }}>{data.highReadiness}%</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-teal-50 rounded">
                      <span className="text-sm">Medium Readiness:</span>
                      <span className="font-medium" style={{ color: colors.mediumReadiness }}>{data.mediumReadiness}%</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">Low Readiness:</span>
                      <span className="font-medium">{data.lowReadiness}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center text-sm text-gray-700">
              <strong>29.4 Point Gender Gap:</strong> Women ready for direct conversion, men require education-first approach
            </div>
          </div>

          {/* Payment & Service Preferences */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Payment & Service Delivery Preferences</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center">
                <DonutChart
                  data={paymentPreferences}
                  colors={[colors.basicCare, colors.standardCare, colors.premiumCare]}
                  title="Payment Preferences"
                  subtitle="High Readiness Segment"
                  size={200}
                />
                <p className="text-xs text-gray-600 mt-2">
                  89% prefer accessible pricing (Basic + Standard Care)
                </p>
              </div>
              
              <div className="text-center">
                <DonutChart
                  data={serviceDelivery}
                  colors={[colors.hospitals, colors.home, colors.provider, colors.lowReadiness]}
                  title="Service Delivery Preferences"
                  subtitle="Multi-Channel Approach"
                  size={200}
                />
                <p className="text-xs text-gray-600 mt-2">
                  71.4% prefer formal healthcare settings
                </p>
              </div>
            </div>
          </div>

          {/* Vaccination Intent Spectrum */}
          <div className="border border-gray-300 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Vaccination Intent Spectrum</h3>
            
            <div className="flex justify-center mb-4">
              <DonutChart
                data={vaccinationIntent}
                colors={[colors.definitely, colors.probably, colors.might, colors.unlikely]}
                title="HPV Vaccination Intent"
                subtitle="Population-Representative Distribution"
                size={240}
              />
            </div>
            
            <div className="text-center text-sm text-gray-700">
              <strong>71.8% Positive Intent:</strong> 59.0% definitely + 12.8% probably willing to get HPV vaccination
            </div>
          </div>

          {/* Strategic Market Segments */}
          <div className="border border-gray-300 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">Strategic Market Segments</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium" style={{ color: colors.highReadiness }}>Immediate Conversion Market</h4>
                <div className="text-2xl font-bold mb-2" style={{ color: colors.highReadiness }}>~91,964</div>
                <ul className="text-sm space-y-1">
                  <li>• <strong>Women High Readiness:</strong> ~47,022 (primary target)</li>
                  <li>• <strong>35-45 High Opportunity:</strong> ~22,655 (expansion potential)</li>
                  <li>• <strong>Hospital-based delivery:</strong> 45.4% preference</li>
                  <li>• <strong>Basic care pricing:</strong> 75.2% preference</li>
                </ul>
              </div>
              
              <div className="bg-teal-50 p-4 rounded-lg">
                <h4 className="font-medium" style={{ color: colors.mediumReadiness }}>Education Intervention Market</h4>
                <div className="text-2xl font-bold mb-2" style={{ color: colors.mediumReadiness }}>~36,133</div>
                <ul className="text-sm space-y-1">
                  <li>• <strong>Men Medium Readiness:</strong> ~16,849 (male education focus)</li>
                  <li>• <strong>Young Adult Targets:</strong> ~19,325 (15-24 engagement)</li>
                  <li>• <strong>Prevention motivated:</strong> 97.2% baseline interest</li>
                  <li>• <strong>Education-responsive:</strong> Bridge prevention ↔ vaccination</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2">Critical Insight: Prevention vs Vaccination Gap</h4>
              <p className="text-sm text-amber-700">
                <strong>97.2% prevention interest</strong> but only <strong>71.8% vaccination willingness</strong> indicates 
                a 25.4 percentage point education opportunity rather than fundamental health motivation barriers. 
                Vaccination-specific education can convert prevention interest into vaccination acceptance.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-4">
          MSD HEPT Nigeria Market Entry Strategy • MRP Population-Adjusted Results • Target Population: 798,000 individuals
        </div>
      </div>
    </div>
  );
};

export default NigeriansVaccinationInterests;